/**
 * Revenue Projection Calculator
 *
 * Interactive what-if projection tool for the Financials (P&L) page.
 * Allows adjusting revenue drivers and cost structure levers to model profitability scenarios.
 * All calculations are client-side (pure math, no API calls).
 */

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ComposedChart,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { getChartColor } from "../config/branding";

// ─────────────────────────────────────────────
// Interfaces
// ─────────────────────────────────────────────

interface ActualsData {
  cashCollected: number;
  closerPayouts: number;
  setterPayouts: number;
  takeHome: number;
  totalExpenses: number;
  netProfit: number;
  callsBooked?: number;
  showRate?: number;
  closeRate?: number;
  avgDealSize?: number;
}

interface ProfitabilityCalculatorProps {
  actuals: ActualsData | null;
}

interface CalculatorInputs {
  // Revenue drivers
  callsBooked: number;      // integer 0–500
  showRate: number;         // decimal 0.0–1.0 (UI: 0–100%)
  closeRate: number;        // decimal 0.0–1.0 (UI: 0–100%)
  avgDealSize: number;      // dollars 0–50000
  // Cost structure
  closerPayoutPct: number;  // decimal 0.0–0.30 (UI: 0–30%)
  setterPayoutPct: number;  // decimal 0.0–0.20 (UI: 0–20%)
  takeHomePct: number;      // decimal 0.0–0.50 (UI: 0–50%)
  fixedExpenses: number;    // dollars 0–100000
}

interface CalculatorOutputs {
  showedUp: number;
  closedDeals: number;
  cashCollected: number;
  closerPayouts: number;
  setterPayouts: number;
  takeHome: number;
  netProfit: number;
  profitMargin: number;
}

interface WaterfallBar {
  name: string;
  offset: number;
  value: number;
  fill: string;
  rawValue: number;
  isDeduction: boolean;
}

interface ComparisonPoint {
  name: string;
  projected: number;
  actual: number;
}

type PresetId = "conservative" | "realistic" | "aggressive";

// ─────────────────────────────────────────────
// Format Utilities
// ─────────────────────────────────────────────

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

const formatCurrencyAbbrev = (value: number): string => {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs}`;
};

// ─────────────────────────────────────────────
// Calculation
// ─────────────────────────────────────────────

function calculateOutputs(inputs: CalculatorInputs): CalculatorOutputs {
  const showedUp = inputs.callsBooked * inputs.showRate;
  const closedDeals = showedUp * inputs.closeRate;
  const cashCollected = closedDeals * inputs.avgDealSize;
  const closerPayouts = cashCollected * inputs.closerPayoutPct;
  const setterPayouts = cashCollected * inputs.setterPayoutPct;
  const takeHome = cashCollected * inputs.takeHomePct;
  const netProfit = takeHome - inputs.fixedExpenses;
  const profitMargin = cashCollected > 0 ? (netProfit / cashCollected) * 100 : 0;

  return {
    showedUp: Math.round(showedUp),
    closedDeals: Math.round(closedDeals),
    cashCollected: Math.round(cashCollected),
    closerPayouts: Math.round(closerPayouts),
    setterPayouts: Math.round(setterPayouts),
    takeHome: Math.round(takeHome),
    netProfit: Math.round(netProfit),
    profitMargin: Math.round(profitMargin * 10) / 10,
  };
}

// ─────────────────────────────────────────────
// Presets
// ─────────────────────────────────────────────

// Base rate assumptions for all presets
const SHOW_RATE_REALISTIC = 0.65;
const CLOSE_RATE_REALISTIC = 0.22;
const MAX_CALLS = 500; // Slider ceiling — keep deal size manageable

/**
 * Derive cost percentages from actual data.
 * Falls back to industry defaults when actuals are absent.
 */
function deriveCostPcts(actuals: ActualsData | null) {
  const cash = actuals?.cashCollected ?? 0;
  return {
    closerPayoutPct:
      cash > 0 && actuals
        ? Math.round((actuals.closerPayouts / cash) * 1000) / 1000
        : 0.10,
    setterPayoutPct:
      cash > 0 && actuals
        ? Math.round((actuals.setterPayouts / cash) * 1000) / 1000
        : 0.05,
    takeHomePct:
      cash > 0 && actuals
        ? Math.round((actuals.takeHome / cash) * 1000) / 1000
        : 0.30,
  };
}

/**
 * Compute avg deal size so that the realistic preset reproduces the actual
 * cash collected: callsBooked × showRate × closeRate × avgDealSize = cashCollected.
 * Caps calls at MAX_CALLS so the deal size stays within a useful slider range.
 */
function computeRealisticDealSize(cashCollected: number): number {
  const impliedCloses = MAX_CALLS * SHOW_RATE_REALISTIC * CLOSE_RATE_REALISTIC;
  const deal = impliedCloses > 0 ? cashCollected / impliedCloses : 5000;
  return Math.max(100, Math.round(deal / 100) * 100);
}

function buildRealisticPreset(actuals: ActualsData | null): CalculatorInputs {
  const cash = actuals?.cashCollected ?? 0;
  const costPcts = deriveCostPcts(actuals);
  return {
    callsBooked: actuals?.callsBooked ?? MAX_CALLS,
    showRate: actuals?.showRate ?? SHOW_RATE_REALISTIC,
    closeRate: actuals?.closeRate ?? CLOSE_RATE_REALISTIC,
    avgDealSize: actuals?.avgDealSize || (cash > 0 ? computeRealisticDealSize(cash) : 5000),
    ...costPcts,
    fixedExpenses: actuals?.totalExpenses ?? 15000,
  };
}

/**
 * Conservative = ~80% volume, slightly worse conversion rates.
 */
function buildConservativePreset(actuals: ActualsData | null): CalculatorInputs {
  const realistic = buildRealisticPreset(actuals);
  return {
    ...realistic,
    callsBooked: Math.round(realistic.callsBooked * 0.8),
    showRate: Math.max(0, realistic.showRate - 0.10),
    closeRate: Math.max(0, realistic.closeRate - 0.05),
  };
}

/**
 * Aggressive = ~120% volume, slightly better conversion rates.
 */
function buildAggressivePreset(actuals: ActualsData | null): CalculatorInputs {
  const realistic = buildRealisticPreset(actuals);
  return {
    ...realistic,
    callsBooked: Math.round(realistic.callsBooked * 1.2),
    showRate: Math.min(1, realistic.showRate + 0.10),
    closeRate: Math.min(1, realistic.closeRate + 0.05),
    avgDealSize: Math.round(realistic.avgDealSize * 1.15 / 100) * 100,
  };
}

// ─────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────

function InputSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="h-3 w-0.5 rounded-full bg-[var(--brand-primary)]" />
        <h4 className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          {title}
        </h4>
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

interface SliderRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: "number" | "percent" | "currency";
  onChange: (value: number) => void;
}

function SliderRow({ label, value, min, max, step, format, onChange }: SliderRowProps) {
  const toText = (v: number) =>
    format === "percent" ? v.toFixed(1) : String(Math.round(v));

  const [inputText, setInputText] = useState(toText(value));

  useEffect(() => {
    setInputText(toText(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, format]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
    const parsed = parseFloat(e.target.value);
    if (!isNaN(parsed)) {
      onChange(Math.min(max, Math.max(min, parsed)));
    }
  };

  const handleBlur = () => {
    const parsed = parseFloat(inputText);
    if (isNaN(parsed)) {
      setInputText(toText(value));
    } else {
      const clamped = Math.min(max, Math.max(min, parsed));
      onChange(clamped);
      setInputText(toText(clamped));
    }
  };

  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground leading-none">{label}</span>
        <div className="flex items-center gap-0.5 shrink-0">
          {format === "currency" && (
            <span className="text-xs text-muted-foreground">$</span>
          )}
          <input
            type="number"
            value={inputText}
            min={min}
            max={max}
            step={step}
            onChange={handleTextChange}
            onBlur={handleBlur}
            className="w-20 text-right text-xs font-semibold text-foreground bg-transparent border-0 outline-none focus:ring-1 focus:ring-[var(--brand-primary)] rounded px-1 py-0.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          {format === "percent" && (
            <span className="text-xs text-muted-foreground">%</span>
          )}
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="calc-slider w-full h-1.5 rounded-full appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, var(--brand-primary) ${pct}%, var(--border) ${pct}%)`,
        }}
      />
    </div>
  );
}

interface OutputCardProps {
  label: string;
  value: number;
  format: "currency" | "percent";
  actual?: number;
  forceNegativeColor?: boolean;
}

function OutputCard({ label, value, format, actual, forceNegativeColor }: OutputCardProps) {
  const formatted =
    format === "currency" ? formatCurrency(value) : `${value.toFixed(1)}%`;

  const delta = actual != null ? value - actual : null;
  const deltaFormatted =
    delta != null
      ? format === "currency"
        ? `${delta >= 0 ? "+" : ""}${formatCurrency(delta)}`
        : `${delta >= 0 ? "+" : ""}${delta.toFixed(1)}%`
      : null;

  const isNegative = forceNegativeColor ?? value < 0;

  return (
    <div className="bg-secondary/30 rounded-xl p-3 border border-border flex flex-col gap-1.5 transition-all duration-200 hover:bg-secondary/50 hover:border-border/80">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground leading-none">
        {label}
      </p>
      <AnimatePresence mode="wait">
        <motion.p
          key={formatted}
          initial={{ opacity: 0, y: -3 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 3 }}
          transition={{ duration: 0.12 }}
          className={`text-lg font-bold leading-none ${isNegative ? "text-red-500" : "text-foreground"
            }`}
        >
          {formatted}
        </motion.p>
      </AnimatePresence>
      {deltaFormatted && (
        <motion.span
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 300, damping: 22 }}
          className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-md w-fit ${delta! >= 0
            ? "bg-emerald-500/10 text-emerald-500"
            : "bg-red-500/10 text-red-500"
            }`}
        >
          {deltaFormatted} vs actual
        </motion.span>
      )}
    </div>
  );
}

function FunnelCard({
  callsBooked,
  showedUp,
  closedDeals,
}: {
  callsBooked: number;
  showedUp: number;
  closedDeals: number;
}) {
  return (
    <div className="bg-secondary/30 rounded-xl p-3 border border-border flex flex-col gap-1.5 transition-all duration-200 hover:bg-secondary/50 hover:border-border/80">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground leading-none">
        Pipeline
      </p>
      <div className="flex items-center gap-1 mt-0.5">
        <div className="text-center flex-1 min-w-0">
          <AnimatePresence mode="wait">
            <motion.p
              key={callsBooked}
              initial={{ opacity: 0, y: -3 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 3 }}
              transition={{ duration: 0.12 }}
              className="text-base font-bold text-foreground leading-none"
            >
              {callsBooked}
            </motion.p>
          </AnimatePresence>
          <p className="text-[9px] text-muted-foreground mt-0.5">Booked</p>
        </div>
        <svg className="w-2.5 h-2.5 text-muted-foreground/40 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
        </svg>
        <div className="text-center flex-1 min-w-0">
          <AnimatePresence mode="wait">
            <motion.p
              key={showedUp}
              initial={{ opacity: 0, y: -3 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 3 }}
              transition={{ duration: 0.12 }}
              className="text-base font-bold text-[var(--brand-primary)] leading-none"
            >
              {showedUp}
            </motion.p>
          </AnimatePresence>
          <p className="text-[9px] text-muted-foreground mt-0.5">Showed</p>
        </div>
        <svg className="w-2.5 h-2.5 text-muted-foreground/40 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
        </svg>
        <div className="text-center flex-1 min-w-0">
          <AnimatePresence mode="wait">
            <motion.p
              key={closedDeals}
              initial={{ opacity: 0, y: -3 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 3 }}
              transition={{ duration: 0.12 }}
              className="text-base font-bold text-emerald-500 leading-none"
            >
              {closedDeals}
            </motion.p>
          </AnimatePresence>
          <p className="text-[9px] text-muted-foreground mt-0.5">Closed</p>
        </div>
      </div>
    </div>
  );
}

// Recharts tooltip types
interface TooltipPayloadEntry {
  payload?: WaterfallBar;
  color?: string;
  value?: number;
  name?: string;
}

interface WaterfallTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
}

function WaterfallTooltip({ active, payload }: WaterfallTooltipProps) {
  if (!active || !payload?.length) return null;
  // payload[1] is the visible bar (index 0 is the transparent spacer)
  const entry = payload[1]?.payload ?? payload[0]?.payload;
  if (!entry) return null;

  return (
    <div className="bg-card border border-border rounded-xl shadow-xl px-3 py-2 text-xs">
      <p className="font-semibold text-foreground">{entry.name}</p>
      <p className="text-muted-foreground mt-0.5">
        {entry.isDeduction ? "−" : ""}
        {formatCurrency(Math.abs(entry.rawValue))}
      </p>
    </div>
  );
}

interface ComparisonPayloadEntry {
  name?: string;
  value?: number;
  color?: string;
}

interface ComparisonTooltipProps {
  active?: boolean;
  payload?: ComparisonPayloadEntry[];
  label?: string;
}

function ComparisonTooltip({ active, payload, label }: ComparisonTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-xl shadow-xl px-3 py-2 text-xs">
      <p className="font-semibold text-foreground mb-1">{String(label ?? "")}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-muted-foreground" style={{ color: entry.color }}>
          {String(entry.name)}: {formatCurrency(entry.value ?? 0)}
        </p>
      ))}
    </div>
  );
}

function WaterfallChart({ data }: { data: WaterfallBar[] }) {
  return (
    <div className="h-52">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
            tickFormatter={formatCurrencyAbbrev}
            width={54}
          />
          <Tooltip content={<WaterfallTooltip />} />
          {/* Transparent spacer — lifts visible bar to the correct position */}
          <Bar
            dataKey="offset"
            stackId="waterfall"
            fill="transparent"
            isAnimationActive={false}
          />
          {/* Visible colored bar */}
          <Bar
            dataKey="value"
            stackId="waterfall"
            radius={[4, 4, 0, 0]}
            isAnimationActive
            animationDuration={600}
            animationEasing="ease-out"
          >
            {data.map((entry, index) => (
              <Cell key={index} fill={entry.fill} />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function ComparisonChart({ data }: { data: ComparisonPoint[] }) {
  const projectedColor = getChartColor(0);
  const actualColor = getChartColor(2);

  return (
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
            tickFormatter={formatCurrencyAbbrev}
            width={54}
          />
          <Tooltip content={<ComparisonTooltip />} />
          <Legend
            iconType="circle"
            iconSize={7}
            wrapperStyle={{ fontSize: "10px", color: "var(--muted-foreground)" }}
          />
          <Bar
            dataKey="projected"
            name="Projected"
            fill={projectedColor}
            radius={[3, 3, 0, 0]}
            barSize={14}
            isAnimationActive
            animationDuration={400}
          />
          <Bar
            dataKey="actual"
            name="Actual"
            fill={actualColor}
            radius={[3, 3, 0, 0]}
            barSize={14}
            isAnimationActive
            animationDuration={400}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────

export function ProfitabilityCalculator({ actuals }: ProfitabilityCalculatorProps) {
  const [inputs, setInputs] = useState<CalculatorInputs>(() =>
    buildRealisticPreset(actuals)
  );
  const [activePreset, setActivePreset] = useState<PresetId | null>("realistic");

  // Seed inputs from actuals the first time real data arrives.
  // The component often mounts before the API response, so the initial
  // useState call runs with actuals=null — this effect corrects that.
  const hasSeededRef = useRef(false);
  useEffect(() => {
    if (actuals && actuals.cashCollected > 0 && !hasSeededRef.current) {
      hasSeededRef.current = true;
      setInputs(buildRealisticPreset(actuals));
      setActivePreset("realistic");
    }
  }, [actuals]);

  // Dynamic slider ceiling for avg deal size based on implied value from actuals.
  // Ensures the slider is always usable even for high-ticket businesses.
  const maxAvgDeal = useMemo(() => {
    if (!actuals?.cashCollected) return 50000;
    const dealSize = actuals.avgDealSize || computeRealisticDealSize(actuals.cashCollected);
    // 2× headroom, rounded up to nearest $5k, minimum $50k
    return Math.max(50000, Math.ceil((dealSize * 2) / 5000) * 5000);
  }, [actuals?.cashCollected, actuals?.avgDealSize]);

  // Dynamic slider ceiling for calls booked
  const maxCallsBooked = useMemo(() => {
    const actualCalls = actuals?.callsBooked || MAX_CALLS;
    return Math.max(500, Math.ceil((actualCalls * 2) / 100) * 100);
  }, [actuals?.callsBooked]);

  // Derived outputs — pure calculation, no side effects
  const outputs = useMemo(() => calculateOutputs(inputs), [inputs]);

  // Waterfall chart data: Revenue → deductions → Take-Home → Net Profit
  const waterfallData = useMemo((): WaterfallBar[] => {
    const { cashCollected, closerPayouts, setterPayouts, takeHome, netProfit } = outputs;

    // "Other" = the revenue that isn't closer pay, setter pay, or take-home
    const otherDeduction = Math.max(0, cashCollected - closerPayouts - setterPayouts - takeHome);

    let floor = cashCollected;

    const revenueBar: WaterfallBar = {
      name: "Revenue",
      offset: 0,
      value: cashCollected,
      fill: getChartColor(2), // Teal
      rawValue: cashCollected,
      isDeduction: false,
    };

    floor -= closerPayouts;
    const closerBar: WaterfallBar = {
      name: "Closer Pay",
      offset: floor,
      value: closerPayouts,
      fill: getChartColor(3), // Amber
      rawValue: closerPayouts,
      isDeduction: true,
    };

    floor -= setterPayouts;
    const setterBar: WaterfallBar = {
      name: "Setter Pay",
      offset: floor,
      value: setterPayouts,
      fill: getChartColor(3), // Amber
      rawValue: setterPayouts,
      isDeduction: true,
    };

    floor -= otherDeduction;
    const otherBar: WaterfallBar = {
      name: "Other",
      offset: floor,
      value: otherDeduction,
      fill: getChartColor(4), // Violet
      rawValue: otherDeduction,
      isDeduction: true,
    };

    const takeHomeBar: WaterfallBar = {
      name: "Take-Home",
      offset: 0,
      value: takeHome,
      fill: getChartColor(1), // Indigo
      rawValue: takeHome,
      isDeduction: false,
    };

    const isPositiveProfit = netProfit >= 0;
    const netProfitBar: WaterfallBar = {
      name: "Net Profit",
      offset: isPositiveProfit ? 0 : netProfit,
      value: Math.abs(netProfit),
      // Semantic red for negative — SVG fills can't use CSS variables reliably
      fill: isPositiveProfit ? getChartColor(5) : "#ef4444",
      rawValue: netProfit,
      isDeduction: !isPositiveProfit,
    };

    return [revenueBar, closerBar, setterBar, otherBar, takeHomeBar, netProfitBar];
  }, [outputs]);

  // Comparison chart data (only meaningful when actuals exist)
  const comparisonData = useMemo((): ComparisonPoint[] => {
    if (!actuals) return [];
    return [
      { name: "Revenue", projected: outputs.cashCollected, actual: actuals.cashCollected },
      { name: "Take-Home", projected: outputs.takeHome, actual: actuals.takeHome },
      { name: "Net Profit", projected: outputs.netProfit, actual: actuals.netProfit },
      { name: "Expenses", projected: inputs.fixedExpenses, actual: actuals.totalExpenses },
    ];
  }, [outputs, actuals, inputs.fixedExpenses]);

  const handleInputChange = useCallback(
    <K extends keyof CalculatorInputs>(key: K, value: CalculatorInputs[K]) => {
      setActivePreset(null);
      setInputs((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const applyPreset = useCallback(
    (presetId: PresetId) => {
      setActivePreset(presetId);
      if (presetId === "conservative") setInputs(buildConservativePreset(actuals));
      else if (presetId === "realistic") setInputs(buildRealisticPreset(actuals));
      else setInputs(buildAggressivePreset(actuals));
    },
    [actuals]
  );

  return (
    <>
      {/* Slider thumb styles — injected once per component mount */}
      <style>{`
        .calc-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--brand-primary);
          border: 2px solid var(--card, white);
          box-shadow: 0 0 0 2px var(--brand-primary);
          cursor: pointer;
          transition: box-shadow 0.15s;
        }
        .calc-slider:hover::-webkit-slider-thumb {
          box-shadow: 0 0 0 5px color-mix(in srgb, var(--brand-primary) 20%, transparent);
        }
        .calc-slider:active::-webkit-slider-thumb {
          box-shadow: 0 0 0 7px color-mix(in srgb, var(--brand-primary) 15%, transparent);
        }
        .calc-slider::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--brand-primary);
          border: 2px solid var(--card, white);
          cursor: pointer;
        }
      `}</style>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="bg-card border border-border rounded-2xl overflow-hidden"
      >
        {/* ── Header ── */}
        <div className="bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-white">Revenue Projection Calculator</h3>
            <p className="text-xs text-white/70 mt-0.5">
              Model scenarios and project your outcomes
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(["conservative", "realistic", "aggressive"] as const).map((preset) => (
              <button
                key={preset}
                onClick={() => applyPreset(preset)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all duration-200 ${activePreset === preset
                  ? "bg-white text-[var(--brand-primary)] shadow-sm"
                  : "bg-white/15 text-white hover:bg-white/25"
                  }`}
              >
                {preset}
              </button>
            ))}
          </div>
        </div>

        {/* ── Body: two-column grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] divide-y lg:divide-y-0 lg:divide-x divide-border">

          {/* ── LEFT: Input Levers ── */}
          <div className="p-6 space-y-7">
            <InputSection title="Revenue Drivers">
              <SliderRow
                label="Calls Booked / Month"
                value={inputs.callsBooked}
                min={0}
                max={maxCallsBooked}
                step={1}
                format="number"
                onChange={(v) => handleInputChange("callsBooked", v)}
              />
              <SliderRow
                label="Show Rate"
                value={inputs.showRate * 100}
                min={0}
                max={100}
                step={1}
                format="percent"
                onChange={(v) => handleInputChange("showRate", v / 100)}
              />
              <SliderRow
                label="Close Rate"
                value={inputs.closeRate * 100}
                min={0}
                max={100}
                step={1}
                format="percent"
                onChange={(v) => handleInputChange("closeRate", v / 100)}
              />
              <SliderRow
                label="Avg Deal Size"
                value={inputs.avgDealSize}
                min={0}
                max={maxAvgDeal}
                step={100}
                format="currency"
                onChange={(v) => handleInputChange("avgDealSize", v)}
              />
            </InputSection>

            <InputSection title="Cost Structure">
              <SliderRow
                label="Closer Payout"
                value={inputs.closerPayoutPct * 100}
                min={0}
                max={30}
                step={0.5}
                format="percent"
                onChange={(v) => handleInputChange("closerPayoutPct", v / 100)}
              />
              <SliderRow
                label="Setter Payout"
                value={inputs.setterPayoutPct * 100}
                min={0}
                max={20}
                step={0.5}
                format="percent"
                onChange={(v) => handleInputChange("setterPayoutPct", v / 100)}
              />
              <SliderRow
                label="Take-Home %"
                value={inputs.takeHomePct * 100}
                min={0}
                max={50}
                step={0.5}
                format="percent"
                onChange={(v) => handleInputChange("takeHomePct", v / 100)}
              />
              <SliderRow
                label="Monthly Fixed Expenses"
                value={inputs.fixedExpenses}
                min={0}
                max={100000}
                step={500}
                format="currency"
                onChange={(v) => handleInputChange("fixedExpenses", v)}
              />
            </InputSection>
          </div>

          {/* ── RIGHT: Outputs + Charts ── */}
          <div className="p-6 space-y-6 min-w-0">

            {/* Output summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <OutputCard
                label="Proj. Revenue"
                value={outputs.cashCollected}
                format="currency"
                actual={actuals?.cashCollected}
              />
              <OutputCard
                label="Net Profit"
                value={outputs.netProfit}
                format="currency"
                actual={actuals?.netProfit}
                forceNegativeColor={outputs.netProfit < 0}
              />
              <OutputCard
                label="Profit Margin"
                value={outputs.profitMargin}
                format="percent"
                actual={actuals?.cashCollected && actuals?.netProfit ? (actuals.netProfit / actuals.cashCollected) * 100 : undefined}
                forceNegativeColor={outputs.profitMargin < 0}
              />
              <FunnelCard
                callsBooked={inputs.callsBooked}
                showedUp={outputs.showedUp}
                closedDeals={outputs.closedDeals}
              />
            </div>

            {/* Revenue Flow waterfall */}
            <div>
              <h4 className="text-xs font-semibold text-foreground mb-3">Revenue Flow</h4>
              <WaterfallChart data={waterfallData} />
            </div>

            {/* Projected vs Actual comparison (only when actuals loaded) */}
            {actuals && comparisonData.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-foreground mb-3">
                  Projected vs Actual
                </h4>
                <ComparisonChart data={comparisonData} />
              </div>
            )}
          </div>

        </div>
      </motion.div>
    </>
  );
}

export default ProfitabilityCalculator;
